import { SearchDriverOptions } from '@elastic/search-ui'
import Connector from '@lib/api-connector'

// export const DEFAULT_CACHE_CONTROL = 'public, s-maxage=3600'
export const DEFAULT_CACHE_CONTROL =
  'private, no-cache, no-store, max-age=0, must-revalidate'
export const DEFAULT_COUNTRY = 'int'
export const CART_COOKIE_NAME = `sublime-cart-id`
export const CART_COOKIE_MAX_AGE = 30 * 24 * 60 * 60 * 1000 // 30 days
export const SUBLIME_DISCOUNT_TOKEN_COOKIE_NAME = `__sublime_d_t`

export enum CHANNEL {
  NORTH_AMERICA = 'north-america',
  UNITED_KINGDOM = 'united-kingdom',
  EUROPE = 'europe',
  INTERNATIONAL = 'international',
}

export const eaChannelMap: Record<string, string> = {
  ch_01jdyxcrfqe58tztcwxcfgbnsf: 'ch_01k09cnmg5ewxt0mz1gz275zsq',
  ch_01jdyxd05fec8868nbnekkj40g: 'ch_01k09cp3khe0tr3aq2pvqep4xd',
  ch_01jdyxdfapewhabmzf5yh46pt4: 'ch_01k09cpsp4evt8d97jtnjf9vh9',
}

export const getChannelId = (channel: CHANNEL) => {
  switch (channel) {
    case CHANNEL.NORTH_AMERICA:
      return 'ch_01jdyxd05fec8868nbnekkj40g'
    case CHANNEL.UNITED_KINGDOM:
      return 'ch_01jdyxdfapewhabmzf5yh46pt4'
    case CHANNEL.EUROPE:
      return 'ch_01jdyxcrfqe58tztcwxcfgbnsf'
    case CHANNEL.INTERNATIONAL:
      return 'ch_01jdyxd798e539kbqp73br5rqm'
    default:
      throw new Error('Invalid channel')
  }
}

export enum STOCK_STATUS {
  IN_STOCK = 'In stock',
  MADE_TO_ORDER = 'Made to order',
  REMOTE_STOCK = 'Remote stock',
  OUT_OF_STOCK = 'Out of stock',
}

export type Country = {
  code: string
  name: string
  languages: string[]
  currencies: string[]
  channel: CHANNEL
  region: string
  ecommerceEnabled: boolean
}
export const COUNTRIES: Country[] = [
  {
    code: 'at',
    name: 'Austria',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'be',
    name: 'Belgium',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'bg',
    name: 'Bulgaria',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'hr',
    name: 'Croatia',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'cy',
    name: 'Cyprus',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: false,
    currencies: ['EUR'],
  },
  {
    code: 'cz',
    name: 'Czech Republic',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'dk',
    name: 'Denmark',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['DKK'],
  },
  {
    code: 'ee',
    name: 'Estonia',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'fi',
    name: 'Finland',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'fr',
    name: 'France',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'de',
    name: 'Germany',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'gr',
    name: 'Greece',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'hu',
    name: 'Hungary',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },

  {
    code: 'ie',
    name: 'Ireland',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'it',
    name: 'Italy',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'lv',
    name: 'Latvia',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'lt',
    name: 'Lithuania',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'lu',
    name: 'Luxembourg',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'nl',
    name: 'Netherlands',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'pl',
    name: 'Poland',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'pt',
    name: 'Portugal',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'ro',
    name: 'Romania',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'sk',
    name: 'Slovakia',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'si',
    name: 'Slovenia',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'es',
    name: 'Spain',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'se',
    name: 'Sweden',
    languages: ['en'],
    channel: CHANNEL.EUROPE,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['EUR'],
  },
  {
    code: 'gb',
    name: 'United Kingdom',
    languages: ['en'],
    channel: CHANNEL.UNITED_KINGDOM,
    region: 'Europe',
    ecommerceEnabled: true,
    currencies: ['GBP'],
  },
  {
    code: 'us',
    name: 'United States',
    languages: ['en'],
    channel: CHANNEL.NORTH_AMERICA,
    region: 'America',
    ecommerceEnabled: true,
    currencies: ['USD'],
  },
  {
    code: 'int',
    name: 'Rest of the World',
    languages: ['en'],
    channel: CHANNEL.INTERNATIONAL,
    region: 'International',
    ecommerceEnabled: false,
    currencies: ['USD'],
  },
]

export const CUSTOMER_GROUPS: Record<string, string> = {
  AD: 'cusgrp_01jjps1p5ye76te2w3msakj83b',
}

export type Region = {
  name: string
  countries: typeof COUNTRIES
}
export const REGIONS: Region[] = COUNTRIES.reduce((acc, country) => {
  const region = acc.find((region) => region.name === country.region)
  if (region) {
    region.countries.push(country)
  } else {
    acc.push({
      name: country.region,
      countries: [country],
    })
  }
  return acc
}, [])

// Elastic search
const connector = new Connector()
export const DEFAULT_ELASTIC_SEARCH_CONFIG: SearchDriverOptions = {
  apiConnector: connector,
  trackUrlState: true,
  alwaysSearchOnInitialLoad: false,
  hasA11yNotifications: true,
  debug: false,
  initialState: {
    resultsPerPage: 100,
  },
  searchQuery: {
    search_fields: {},
    result_fields: {},

    facets: {
      _index: {
        type: 'value',
        size: 10,
      },
    },
    resultsPerPage: 100,
  },
}
